import { DateTime } from 'luxon';

import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
	getCJSSearchResponse,
	getCJSSearchUrl,
	getFlydriveDestinations
} from '../../lib/utils/api.service';
import {
	DATE_FORMATS,
	DATEPICKER_ERROR_MESSAGE
} from '../../lib/utils/constants';
import { RoomComposition, Rooms } from '../../lib/utils/composition.service';
import handleInputFocus from '../../lib/utils/handle-input-focus';

import { useApplication } from '../../hooks/useApplication';

import {
	CJSSearchRequest,
	FlyDriveDestination,
	Gateway,
	LocationParams
} from '../../types/api';
import { BookingType } from '../../types/config';
import { Criteria, FLYDRIVE, PRODUCT_TYPES } from '../../types/criteria';

import {
	Div,
	FormGrid,
	column,
	destination,
	date,
	passengers,
	readOnly,
	submit,
	ServerError
} from './forms.styles';

import SeasonalMessageComponent from '../seasonal-message/seasonal-message.component';
import DatePickerFieldComponent from '../date-picker-field/date-picker-field.component';
import ListFieldComponent from '../list-field/list-field.component';
import RoomFieldComponent from '../room-field/room-field.component';

import {
	ButtonComponent,
	ButtonSize,
	ButtonType
} from '@vaa-component-lib/component.atom.button';
import {
	AlertComponent,
	AlertEmphasis,
	AlertStatus
} from '@vaa-component-lib/component.molecule.alert';
import {
	IconActionSearchComponent,
	IconTravelTakeOffComponent,
	IconTravelGlobeMarkerComponent
} from '@vaa-component-lib/component.atom.icons';
import DynamicListSelectorComponent from '../dynamic-list-selector/dynamic-list-selector.component';
import { inputWidths, mainColumn } from './holiday.styles';
import HolidayNavComponent from '../holiday-nav/holiday-nav.component';
import HolidayInfoComponent from '../holiday-info/holiday-info.component';
import { refsToUrlParams } from '../../lib/utils/refs';

interface FlightCarComponentProps {
	bookingType: BookingType;
	selectSubBookingType: any;
}

const FlightCarComponent = ({
	bookingType,
	selectSubBookingType
}: FlightCarComponentProps) => {
	const { application, setApplication } = useApplication();
	const [submitting, setSubmitting] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);
	const criteria: FLYDRIVE | null =
		bookingType.type === application.criteria?.searchType
			? (application.criteria as FLYDRIVE)
			: null;

	const formMethods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onChange'
	});

	const refUrl = 'VA_NHP_SEARCH';
	const [destinationList, setDestinationList] = useState<FlyDriveDestination[]>(
		[]
	);
	const [gateways, setGateways] = useState<Gateway[] | []>(
		(criteria as FLYDRIVE)?.gateways || []
	);
	const [onSaleStart, setOnSaleStart] = useState<string>('');
	const [onSaleEnd, setOnSaleEnd] = useState<string>('');

	if (criteria && !criteria?.refs?.['refUrl']) {
		criteria.refs = {};
		criteria.refs['refUrl'] = 'VA_NHP_SEARCH';
	}

	const {
		register,
		control,
		setError,
		handleSubmit,
		formState: { isSubmitting, errors },
		watch
	} = formMethods;

	const onSubmit = (data: any, error: any) => {
		setSubmitting(true);
		const query: CJSSearchRequest = {
			brand: bookingType.brand,
			submissionType: PRODUCT_TYPES.FLYDRIVE,
			location: data.location,
			departureDate: DateTime.fromISO(data.departing[0]).toFormat(
				DATE_FORMATS.URL_DATE
			),
			duration: DateTime.fromISO(data.departing[1]).diff(
				DateTime.fromISO(data.departing[0]),
				'days'
			).days,
			partyCompositions: Rooms.format(data.passengers),
			...(data.gateway && { gateway: data.gateway })
		};
		getCJSSearchUrl(query)
			.then((link) => {
				const url = `${link.data.url}${refsToUrlParams(criteria?.refs)}`;
				window.open(url, '_self');
			})
			.catch((error) => {
				setSubmitting(false);
				setError('root.serverError', {
					type: 'custom',
					message:
						"Oops! We're having some technical issues, please pop back later."
				});
			});
	};

	const watchLocation: string = watch('location');
	const flyDriveParams: LocationParams = {
		brand: bookingType.brand,
		bookingType: PRODUCT_TYPES.FLYDRIVE
	};

	useEffect(() => {
		if (watchLocation && watchLocation !== '') {
			getCJSSearchResponse(watchLocation, flyDriveParams)
				.then((response) => {
					response?.data?.gateways && setGateways(response.data.gateways);
					response?.data?.onSaleStart &&
						setOnSaleStart(response.data.onSaleStart);
					response?.data?.onSaleEnd && setOnSaleEnd(response.data.onSaleEnd);
				})
				.catch((error) => {
					setGateways([]);
				});
		}
	}, [watchLocation]);

	useEffect(() => {
		getFlydriveDestinations()
			.then((response) => setDestinationList(response?.data?.destinations))
			.catch((error) => {
				setError('root.serverError', {
					type: 'custom',
					message:
						"Oops! We're having some technical issues, please pop back later."
				});
				setDestinationList([]);
			});
	}, []);

	return (
		<form
			id={bookingType?.type?.toString()}
			data-testid={`search-form-${bookingType?.type?.toString()}`}
			onSubmit={handleSubmit(onSubmit)}>
			<FormProvider {...formMethods}>
				{errors?.root?.serverError?.type === 'custom' && (
					<ServerError>
						<AlertComponent
							emphasis={AlertEmphasis.Low}
							hasIcon
							status={AlertStatus.Error}
							title={errors?.root?.serverError?.message}>
							{''}
						</AlertComponent>
					</ServerError>
				)}
				<SeasonalMessageComponent />
				<HolidayNavComponent
					brand={bookingType.brand}
					selectedBookingType={bookingType}
					change={selectSubBookingType}
				/>
				<FormGrid>
					<Div css={[column, mainColumn, destination(inputWidths), readOnly]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<DynamicListSelectorComponent
									options={destinationList?.map((destination) => {
										return {
											group: destination.group,
											options: destination.options.map((option) => {
												return {
													label: option.description,
													value: option.value,
													selectedLabel: option.description,
													selected:
														option.value ==
														criteria?.location?.locationUrlDescription
												};
											})
										};
									})}
									label={'Destination'}
									id={'destination_selector'}
									placeholder="Select a destination"
									ready={destinationList && destinationList.length > 0}
									hasGroups={true}
									hasError={Boolean(errors?.location)}
									feedback={errors?.location?.message?.toString()}
									switchFocusTo={() => handleInputFocus('flight_car_gateway')}
									iconChild={<IconTravelGlobeMarkerComponent />}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'location'}
							defaultValue={''}
							control={control}
							rules={{
								required: 'Please select a destination'
							}}
						/>
					</Div>

					<Div css={[column, mainColumn, destination(inputWidths), readOnly]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<ListFieldComponent
									label={'Flying from'}
									id={'flight_car_gateway'}
									iconChild={<IconTravelTakeOffComponent />}
									feedback={errors?.gateway?.message?.toString()}
									hasError={Boolean(errors?.gateway)}
									options={gateways?.map((gateway) => ({
										label: gateway.description,
										value: gateway.code,
										selected: gateway.code === criteria?.gateway?.code
									}))}
									isDisabled={!watchLocation}
									switchFocusTo={() => handleInputFocus('flight_car_departing')}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'gateway'}
							defaultValue={(criteria as FLYDRIVE)?.gateway || null}
							control={control}
							rules={{
								required: 'Please select where you are flying from'
							}}
						/>
					</Div>

					<Div
						ref={ref}
						css={[column, mainColumn, date(inputWidths), readOnly]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<DatePickerFieldComponent
									label={'When'}
									id={'flight_car_departing'}
									feedback={errors?.departing?.message?.toString()}
									hasError={Boolean(errors?.departing)}
									bookingType={bookingType?.type}
									enabledDateRange={[onSaleStart, onSaleEnd]}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'departing'}
							defaultValue={[
								...((criteria as FLYDRIVE)?.departureDate?.isValid
									? [
											(criteria as FLYDRIVE)?.departureDate,
											(criteria as FLYDRIVE)?.departureDate.plus({
												days: (criteria as FLYDRIVE)?.duration
											})
										]
									: [])
							]}
							control={control}
							rules={{
								validate: {
									requiredDate: (value) => {
										const duration = (criteria as FLYDRIVE)?.duration;

										if (
											duration > 28 ||
											(value.length > 1 &&
												DateTime.fromISO(value[1]).diff(
													DateTime.fromISO(value[0]),
													'days'
												).days > 28)
										) {
											return DATEPICKER_ERROR_MESSAGE.duration;
										}
										if (value.length < 1)
											return DATEPICKER_ERROR_MESSAGE.noSelection;
										if (value.length !== 2)
											return DATEPICKER_ERROR_MESSAGE.range;
									}
								}
							}}
						/>
					</Div>

					<Div css={[column, mainColumn, passengers(inputWidths), readOnly]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<RoomFieldComponent
									label={'Who'}
									placeholder={'Who?'}
									id={'flight_car_who'}
									feedback={errors?.passengers?.message?.toString()}
									hasError={Boolean(errors?.passengers)}
									roomsDisabled={true}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'passengers'}
							defaultValue={null}
							control={control}
							rules={{
								required: 'This field is required',
								validate: (value: RoomComposition[]) => {
									const messages = [];
									const adults = value.reduce(
										(total, composition) => (total += composition.adults),
										0
									);
									const infants = value.reduce(
										(total, composition) => (total += composition.infants),
										0
									);
									const noAge = value.reduce(
										(total, composition) =>
											(total += composition.childAges.filter(
												(age) => !age
											).length),
										0
									);
									const total = value.reduce(
										(total, composition) =>
											(total +=
												composition.adults +
												composition.childAges.length +
												composition.infants),
										0
									);

									if (noAge > 0) {
										messages.push('Please enter all child ages.');
									}
									if (infants > adults) {
										messages.push(
											'There must be at least one adult per child under two years of age.'
										);
									}
									if (total > 9) {
										messages.push(
											'We love a group! For bookings of more than 9 passengers please call our specialists on 0344 472 1422.'
										);
									}

									return messages.length <= 0 ? true : messages.join('\n');
								}
							}}
						/>
					</Div>
					<Div css={[column, mainColumn, submit(inputWidths)]}>
						<ButtonComponent
							data-cy="submit-button"
							type={ButtonType.Submit}
							disabled={isSubmitting}
							size={ButtonSize.Medium}
							iconChild={<IconActionSearchComponent />}
							text={'Search'}
							isLoading={submitting}
							loadingText="Search"
						/>
					</Div>
				</FormGrid>
				<input
					readOnly
					type="hidden"
					defaultValue={bookingType?.type}
					{...register('submissionType')}
				/>
				<input
					readOnly
					type="hidden"
					defaultValue={application?.brand}
					{...register('brand')}
				/>
				<HolidayInfoComponent />
			</FormProvider>
		</form>
	);
};

export default FlightCarComponent;
